// utils/skuGenerator.js
//
// SKU FORMAT:   <CATEGORY CODE>-<NNN>-<VARIATION CODE>
//
//   CATEGORY CODE  -> from the CATEGORY name (level letter + product letter(s))
//   NNN            -> running number of the shop: 001, 002, 003 ... The real number is
//                     given by the backend when the product is saved and a number that was
//                     given once is never given again (even if that product is deleted).
//   VARIATION CODE -> from the VARIATION name (the Type is not used for it)
//
// Level letter:  M = Multiunit, T = Tissue level, I = Implant level
//
//   Ti-base         <level>T + E / NE     Implant Level Ti-base Hex / Engaging -> ITE
//   Scanbody        <level>B              Multiunit Scanbody -> MB, Tissue Level Scanbody -> TB
//   Pin Scanbody    <level>PB             Multiunit Pin Scanbody -> MPB
//   Lab Analog      <level>L              Multiunit Lab Analog -> ML
//   Screw           <level>S              Multiunit Screw -> MS
//   Abutment        <level>A + A/S + angle  Multiunit Angled Abutment 30° -> MAA30
//                                           Multiunit Straight Abutment   -> MAS
//   Sleeve          SL
//   Implant         IM
//   Other           first 2 letters of the category name
//
//   Variation:  GH1 CH8 -> 18, GH1 CH8.5 -> 18, GH2.5 -> 25, GH4.5 -> 45,
//               Narrow/Long/Wide/Extra Wide -> N/L/W/EW, 5.0 -> 50, 4.0 -> 40,
//               Straight Screw -> SS, Angle Correction -> AC,
//               Straight Rosen -> SR, Angled Rosen -> AR
//
// The same rules are used by the backend (product/sku.py).

const normalize = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[_\-/,.°]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Extract a value from either a string or an object.
 */
const getName = (value) => {
  if (!value) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return (
    value.category_Name ||
    value.category_name ||
    value.type_name ||
    value.variation_name ||
    value.name ||
    ""
  );
};

/* M = Multiunit, T = Tissue level, I = Implant level ('' = unknown) */
const getLevelLetter = (...texts) => {
  for (const text of texts) {
    const t = normalize(text);
    if (t.includes("multi")) return "M";
    if (t.includes("tissue")) return "T";
    if (t.includes("implant")) return "I";
  }
  return "";
};

/* 'NE' = non-hex / non-engaging, 'E' = hex / engaging, '' = not mentioned */
const getEngagingPart = (...texts) => {
  for (const text of texts) {
    const t = normalize(text);
    if (/\bnon ?(hex|engaging)/.test(t)) return "NE";
    if (/\b(hex|engaging)\b/.test(t)) return "E";
  }
  return "";
};

/**
 * Category code: MS, ITE, MAA30, SL, IM ...
 */
export const getCategoryCode = (categoryName, typeName = "", variationName = "") => {
  if (!categoryName) return "PR";

  const cat = normalize(categoryName);
  const level = getLevelLetter(categoryName, typeName);

  // Ti-base: <level>T + E / NE
  if (cat.includes("tibase") || cat.includes("ti base")) {
    return `${level}T${getEngagingPart(categoryName, typeName, variationName)}`;
  }

  // Scanbody / pin scanbody
  if (cat.includes("scanbody") || cat.includes("scan body")) {
    return `${level}${/\bpin\b/.test(cat) ? "PB" : "B"}`;
  }

  // Lab analog
  if (cat.includes("analog")) {
    return `${level}L`;
  }

  // Screw
  if (cat.includes("screw")) {
    return level ? `${level}S` : "SC";
  }

  // Abutment: <level>A + (A angled | S straight) + angle digits
  if (cat.includes("abutment")) {
    let code = `${level || "M"}A`;
    if (cat.includes("angle")) code += "A";
    else if (cat.includes("straight")) code += "S";

    const digits = String(categoryName).match(/(\d+)/);
    if (digits) code += digits[1];
    return code;
  }

  // Sleeve
  if (cat.includes("sleeve")) {
    return "SL";
  }

  // Implant (dental implant)
  if (cat.includes("implant")) {
    return "IM";
  }

  const letters = String(categoryName).replace(/[^A-Za-z]/g, "");
  return (letters.slice(0, 2) || "PR").toUpperCase();
};

/**
 * Variation code (the Type is never used here).
 */
export const getVariationCode = (variationName = "", categoryName = "") => {
  let text = String(variationName || "").trim();
  if (!text) return "";

  // For Ti-base the Hex / Engaging words are already part of the category code
  const cat = normalize(categoryName);
  if (cat.includes("tibase") || cat.includes("ti base")) {
    text = text
      .replace(/non[\s-]*hex|non[\s-]*engaging|hex|engaging/gi, " ")
      .replace(/[\s/-]+/g, " ")
      .trim();
    if (!/[A-Za-z0-9]/.test(text)) return "";
  }

  const gh = text.match(/gh\s*(\d+(?:\.\d+)?)/i);
  const ch = text.match(/ch\s*(\d+(?:\.\d+)?)/i);

  if (gh && ch) return `${gh[1].split(".")[0]}${ch[1].split(".")[0]}`;
  if (gh) return gh[1].replace(".", "");
  if (ch) return ch[1].replace(".", "");

  const lower = text.toLowerCase();
  if (/extra\s*wide/.test(lower)) return "EW";
  if (/\bnarrow\b/.test(lower)) return "N";
  if (/\blong\b/.test(lower)) return "L";
  if (/\bwide\b/.test(lower)) return "W";

  const numbers = text.match(/\d+(?:\.\d+)?/g);
  if (numbers) return numbers.map((n) => n.replace(".", "")).join("");

  const words = text.match(/[A-Za-z]+/g);
  if (words) return words.map((w) => w[0]).join("").toUpperCase();

  return "";
};

/* ---------- running numbers ---------- */

// TTE-00118 / BH-002W / MS-482-SS  ->  118 / 2 / 482
const SERIAL_PATTERN = /^[A-Za-z0-9]+-(\d{3,})(?=[A-Za-z]|-|$)/;

export const getUsedSerials = (products = []) => {
  const used = new Set();

  products.forEach((product) => {
    const sku = product?.SKU || product?.sku || "";
    const match = String(sku).match(SERIAL_PATTERN);
    if (match) {
      const serial = parseInt(match[1], 10);
      if (Number.isFinite(serial)) used.add(serial);
    }
  });

  return used;
};

/**
 * The next running number as text ("002").
 * This is only an estimate for screens that need to show a full SKU; the backend
 * gives the real number when the product is saved.
 */
export const getNextSkuSerial = (products = []) => {
  const used = getUsedSerials(products);
  let next = (products?.length || 0) + 1;
  while (used.has(next)) next += 1;
  return String(next).padStart(3, "0");
};

const resolveParts = (typeNameOrOptions, variationName, categoryName, products) => {
  let typeName = typeNameOrOptions;
  let wantedSerial = "";

  if (typeNameOrOptions && typeof typeNameOrOptions === "object") {
    const options = typeNameOrOptions;

    typeName = getName(options.typeName || options.type);
    variationName = getName(options.variationName || options.variation);
    categoryName = getName(options.categoryName || options.category);
    products = options.products || [];
    wantedSerial = options.serial || "";
  }

  const category = getName(categoryName);
  const type = getName(typeName);
  const variation = getName(variationName);

  return {
    prefix: getCategoryCode(category, type, variation),
    suffix: getVariationCode(variation, category),
    products: products || [],
    wantedSerial,
  };
};

/**
 * SKU shown in the "Add Product" form BEFORE saving:  MS-###-SS
 * The number (001, 002 ...) is added by the backend when the product is saved.
 */
export const previewProductSku = (options = {}) => {
  const { prefix, suffix } = resolveParts(options, "", "", []);
  const base = `${prefix}-###`;
  return suffix ? `${base}-${suffix}` : base;
};

/**
 * Full SKU (for screens that need one):  MS-002-SS
 *
 * Supports both:
 *   generateProductSku(type, variation, category, productName, products)
 * and:
 *   generateProductSku({ categoryName, typeName, variationName, products, serial })
 *
 * "serial" is optional. Without it the next running number of the given product
 * list is used.
 */
export const generateProductSku = (
  typeNameOrOptions,
  variationName = "",
  categoryName = "",
  productName = "",
  products = []
) => {
  const { prefix, suffix, products: list, wantedSerial } = resolveParts(
    typeNameOrOptions,
    variationName,
    categoryName,
    products
  );

  const used = getUsedSerials(list);

  let serial = parseInt(wantedSerial, 10);
  if (!Number.isFinite(serial) || serial <= 0 || used.has(serial)) {
    serial = parseInt(getNextSkuSerial(list), 10);
  }

  const base = `${prefix}-${String(serial).padStart(3, "0")}`;
  return suffix ? `${base}-${suffix}` : base;
};

/**
 * Compatibility helper for existing imports.
 */
export const getScrewSku = (variationName = "") => {
  const variation = normalize(variationName);

  if (variation.includes("angle correction")) {
    return "CR-002-AC";
  }

  if (variation.includes("angle rosen") || variation.includes("angled rosen")) {
    return "CR-003-AR";
  }

  if (variation.includes("rosan") || variation.includes("straight rosen")) {
    return "CR-004-SR";
  }

  if (variation.includes("straight")) {
    return "CR-001-SI";
  }

  return "";
};

export default generateProductSku;